import _ from 'underscore';
import {
	isBefore,
	format,
	startOfDay,
	isEqual,
	endOfDay,
	parseISO
} from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import DayPicker, { LocaleUtils } from 'react-day-picker';
import { weekdaysShort } from '@culture/common-helpers/date';

import TopBar from './components/TopBar';

import styles from './styles.module.scss';

const DatePicker = ({
	setNewDates,
	availableStartDate,
	availableDates,
	startDate,
	endDate,
	numberOfMonths
}) => {
	const [dates, setDates] = useState({});

	useEffect(() => {
		if (!_.isEmpty(dates)) {
			setNewDates(dates);
		}
	}, [dates]);

	const modifiers = useMemo(
		() => ({
			start: dates.startDate || (_.isEmpty(dates) && startDate),
			end: dates.endDate || (_.isEmpty(dates) && endDate),
			disabled: (day) => {
				const startDay = startOfDay(day);
				return availableDates
					? !_(availableDates).find(
							(availableDate) =>
								format(new Date(availableDate), 'yyyy-MM-dd') ===
								format(startDay, 'yyyy-MM-dd')
					  )
					: isBefore(startOfDay(day), startOfDay(availableStartDate));
			}
		}),
		[dates, startDate, endDate, availableDates, availableStartDate]
	);

	const onDateChange = useCallback(
		(date, { disabled }) => {
			if (!disabled) {
				const startOfDayDate = startOfDay(date);
				let newEndDate;

				if (modifiers.start) {
					if (modifiers.end || isBefore(date, modifiers.start)) {
						setDates({
							startDate: startOfDayDate,
							endDate: null
						});
					} else if (!isEqual(startOfDayDate, startOfDay(modifiers.start))) {
						newEndDate = endOfDay(date);
					}
				} else {
					setDates((prevDates) => ({
						...prevDates,
						startDate: startOfDayDate
					}));
				}

				if (newEndDate) {
					setDates((prevDates) => ({ ...prevDates, endDate: newEndDate }));
				}
			}
		},
		[modifiers]
	);

	const selectedDays = modifiers.start && [
		modifiers.start,
		{ from: modifiers.start, to: modifiers.end }
	];

	const renderDay = useCallback(
		(day) => <div className="DayPicker-DayText">{day.getDate()}</div>,
		[]
	);

	const dayPickerCx = cx({
		'DayPicker--single-day': !modifiers.end,
		'DayPicker--two-days': modifiers.end
	});

	const fromMonth = isBefore(startDate, availableStartDate)
		? startDate
		: availableStartDate;

	const initialMonth =
		startDate ||
		(!_.isEmpty(availableDates) ? parseISO(availableDates[0]) : fromMonth);

	return (
		<div className={styles.DatePicker}>
			<DayPicker
				className={dayPickerCx}
				navbarElement={TopBar}
				captionElement={() => null}
				renderDay={renderDay}
				modifiers={modifiers}
				onDayClick={onDateChange}
				initialMonth={initialMonth}
				selectedDays={selectedDays}
				fromMonth={fromMonth}
				localeUtils={LocaleUtils}
				locale="ru"
				showOutsideDays
				weekdaysShort={weekdaysShort}
				firstDayOfWeek={1}
				numberOfMonths={numberOfMonths}
			/>
		</div>
	);
};

DatePicker.propTypes = {
	setNewDates: PropTypes.func,
	startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	availableStartDate: PropTypes.object,
	numberOfMonths: PropTypes.number,
	availableDates: PropTypes.arrayOf(PropTypes.string)
};

DatePicker.defaultProps = {
	numberOfMonths: 1,
	availableStartDate: new Date()
};

export default DatePicker;
